import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { MyButton, MyLoadingData } from '@interstellar-component'

import { BillingService } from '../../billing.service'
import { formatIDR, invoiceStatusBadge } from '../../billing.format'

const PAGE_SIZE = 10

// A top-up / platform-fee / enquiry payment is an INVOICE, not a saldo movement,
// so it never enters the "Saldo usage history" ledger — that view only lists
// events that actually moved spendable saldo (an unpaid or canceled top-up moved
// nothing). On the single-company landing this used to leave a canceled top-up
// with no visible home at all: the user cancels a payment and it seemingly
// vanishes (Everest #z94x8e0mgh). This card is that home — every invoice, any
// status, so a canceled/expired top-up is plainly accounted for.
const INVOICE_TYPE_LABEL = {
  saldo_topup: 'Top up saldo',
  subscription: 'Platform fee',
  topup: 'Top up quota',
  enquiry: 'Enquiry report',
}

function invoiceTypeLabel(inv) {
  if (inv?.invoice_type === 'topup' && inv?.quota_amount) {
    return `Top up ${inv.quota_amount} quota`
  }
  return INVOICE_TYPE_LABEL[inv?.invoice_type] || 'Payment'
}

function formatDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

function formatTime(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
}

function StatusBadge({ status }) {
  const { cls, label } = invoiceStatusBadge(status)
  return (
    <span
      className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-md border px-2 py-0.5 text-xs-medium ${cls}`}
    >
      {label}
    </span>
  )
}

StatusBadge.propTypes = { status: PropTypes.string }
StatusBadge.defaultProps = { status: null }

/**
 * "Riwayat pembayaran" — every invoice this company has raised (top-up saldo,
 * platform fee, kuota, per-enquiry), in any status. Complements the saldo ledger
 * above: the ledger answers "how did my balance move?", this answers "what did I
 * pay / try to pay, and what happened to it?" — including cancellations, which
 * the ledger by design does not record.
 */
function PaymentHistory({ companyId, refreshKey }) {
  const [rows, setRows] = useState([])
  const [paginator, setPaginator] = useState(null)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    if (!companyId) return
    setLoading(true)
    setError(null)
    try {
      const res = await BillingService.getInvoices(companyId, { page, limit: PAGE_SIZE })
      // { data, paginator } proxied verbatim — one level deep.
      const body = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res
      setRows(Array.isArray(body?.data) ? body.data : [])
      setPaginator(body?.paginator || null)
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load payment history')
      setRows([])
      setPaginator(null)
    } finally {
      setLoading(false)
    }
  }, [companyId, page])

  useEffect(() => {
    load()
  }, [load, refreshKey])

  const totalPages = paginator?.total_pages || 0
  const total = paginator?.total ?? 0

  return (
    <section className="overflow-hidden rounded-xl border border-gray/200 bg-white shadow-xs">
      <div className="flex items-center gap-2 px-5 py-4">
        <h3 className="text-lg-semibold text-gray/900">Payment history</h3>
        <span className="rounded-full border border-brand/200 bg-brand/50 px-2 py-0.5 text-xs-medium text-brand/700">
          {total.toLocaleString('id-ID')} item
        </span>
      </div>

      <div className="overflow-x-auto border-t border-gray/200">
        <table className="w-full min-w-[720px] border-collapse">
          <thead>
            <tr className="border-b border-gray/200 bg-gray/50">
              <th className="px-5 py-3 text-left text-xs-medium text-gray/600">Invoice</th>
              <th className="px-5 py-3 text-left text-xs-medium text-gray/600">Type</th>
              <th className="px-5 py-3 text-left text-xs-medium text-gray/600">Date</th>
              <th className="px-5 py-3 text-right text-xs-medium text-gray/600">Total</th>
              <th className="px-5 py-3 text-left text-xs-medium text-gray/600">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="px-5 py-12">
                  <MyLoadingData />
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={5} className="px-5 py-10 text-center text-sm-medium text-error/700">
                  {error}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-5 py-10 text-center text-sm-regular text-gray/500">
                  No payment history yet.
                </td>
              </tr>
            ) : (
              rows.map((inv) => (
                <tr key={inv.id} className="border-b border-gray/200 last:border-0">
                  <td className="px-5 py-4 text-sm-medium text-gray/900">
                    {inv.invoice_number || '—'}
                  </td>
                  <td className="px-5 py-4 text-sm-regular text-gray/600">
                    {invoiceTypeLabel(inv)}
                  </td>
                  <td className="px-5 py-4 text-sm-regular text-gray/600">
                    {/* Show the time too, matching the Saldo usage history table
                        (Everest bug: "tidak ada data waktu seperti di tabel Saldo
                        usage history"). */}
                    <div className="text-sm-medium text-gray/900">{formatDate(inv.created_at)}</div>
                    <div className="text-sm-regular text-gray/600">{formatTime(inv.created_at)}</div>
                  </td>
                  <td className="whitespace-nowrap px-5 py-4 text-right text-sm-medium text-gray/900">
                    {formatIDR(inv.total_idr)}
                  </td>
                  <td className="px-5 py-4">
                    <StatusBadge status={inv.status} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 ? (
        <div className="flex items-center justify-between gap-3 border-t border-gray/200 px-5 py-4">
          <span className="text-sm-medium text-gray/700">
            Page {paginator.page} of {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <MyButton
              color="secondary"
              variant="outlined"
              size="sm"
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <span className="text-sm-semibold">Previous</span>
            </MyButton>
            <MyButton
              color="secondary"
              variant="outlined"
              size="sm"
              type="button"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              <span className="text-sm-semibold">Next</span>
            </MyButton>
          </div>
        </div>
      ) : null}
    </section>
  )
}

PaymentHistory.propTypes = {
  companyId: PropTypes.string,
  refreshKey: PropTypes.number,
}

PaymentHistory.defaultProps = {
  companyId: null,
  refreshKey: 0,
}

export default PaymentHistory
